/**
 * Integrations, as a grid of tiles.
 *
 * WHAT CHANGED AND WHY. This was a stack of full-width rows, each carrying its
 * own credential form and a paragraph of explanation, with a small initials
 * square where a logo should be. Damon's notes, in order: the marks want to be
 * roughly five times the size and they should be the vendors' real logos; the
 * configuration belongs BEHIND the tile; the prose can go; Resend is not a
 * backup source so it should not sit among them; and the detail opening below
 * the grid "is just not useful" - it wants to be a modal.
 *
 * So the grid answers one question - what is connected, and is it working -
 * and it answers it without being read: a logo, and two or three badges under
 * it. Everything else opens in a dialog over the top.
 *
 * ONE OPEN AT A TIME, on purpose. Several open forms is how somebody types a
 * credential into the wrong vendor, and every one of these fields is a
 * password box that shows nothing back.
 */

import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { errText, ago } from './btApi';
import { VendorRegistryPanel } from './vendors';
import { PsaPanel } from './psa';
import { VendorTile, type TileBadge } from './components/VendorTile';
import { Modal } from './components/Modal';
import { ConnectionPicker, DEFAULT_CONNECTION } from './components/Connections';
import { EmailIngest } from './components/EmailIngest';

function call<TIn extends object, TOut>(name: string) {
  return async (data: TIn): Promise<TOut> => (await httpsCallable<TIn, TOut>(functions, name)(data)).data;
}

export interface IntegrationField {
  key: string;
  label: string;
  isSet: boolean;
  /** false = ordinary config, shown in the clear. Credentials stay write-only. */
  secret?: boolean;
  value?: string | null;
  placeholder?: string | null;
}
export interface Integration {
  source: 'datto' | 'sp' | 'sc' | 'msp' | 'unitrends' | 'resend';
  label: string;
  enabled: boolean;
  baseUrl: string;
  fields: IntegrationField[];
  configured: boolean;
  updatedAt: string | null;
  lastTestOk: boolean | null;
  lastTestAt: string | null;
  lastTestError: string | null;
  /** Initials on a colour, from the server. Used when we have no real logo. */
  mark?: { initials: string; colour: string; ink: string };
}

const getIntegrations = call<{ orgId: string }, { integrations: Integration[] }>('getOrgIntegrations');
const setIntegration = call<{
  orgId: string; source: string; connectionKey?: string; enabled: boolean; baseUrl: string; values: Record<string, string>;
}, { ok: boolean; fieldsWritten: string[] }>('setOrgIntegration');
const clearIntegration = call<{ orgId: string; source: string; connectionKey?: string }, { ok: boolean }>('clearOrgIntegration');
const testIntegration = call<{ orgId: string; source: string; connectionKey?: string }, {
  ok: boolean; detail: string; error: string | null;
}>('testOrgIntegration');

/**
 * Resolve MSP360's status enum from live data. The JSON representation returns
 * Status as an integer and the vendor does not publish what the integers mean;
 * the XML representation of the SAME endpoint spells them out by name. Reading
 * both and pairing them turns the mapping from an assumption into an
 * observation. Read-only.
 */
const probeMsp = call<{ orgId: string }, {
  ok: boolean;
  message: string;
  planRows: number;
  computers: number;
  statusMap: Record<string, string>;
  sample: string;
}>('probeMsp360Status');

/**
 * Read what Datto actually reports per agent. Which fields carry a schedule is
 * not documented, so this prints what is there and the classifier is written
 * against the answer. Read-only.
 */
const probeDatto = call<{ orgId: string }, { ok: boolean; report: string }>('probeDattoSchedule');

/**
 * Ask the CMD server what this token can reach - without reaching for any of
 * it. GET and OPTIONS only, and no path containing an action verb is fetched
 * at all: some appliances treat GET /reboot as a command, and "we only sent a
 * GET" is not a defence anybody wants to have to make.
 */
const probeScActions = call<{ orgId: string }, { ok: boolean; report: string }>('probeShadowControlActions');

const probeToken = call<{ orgId: string; source: string }, {
  ok: boolean;
  httpStatus: number | null;
  shape: string;
  count: number | null;
  sample: unknown;
  tree: string;
  census: string;
  folderDetail: string;
  spDiff: string;
  scDiff: string;
  schedDiff: string;
  anomalies: string;
  rawSchedule: string;
  rawNoJob: string;
  message: string;
  live: { sp: number; sc: number };
  liveSplit: { sp: string; sc: string };
}>('probeShadowControlToken');

/**
 * Sources with a bespoke tile that must NOT also render generically.
 * Anything absent from this list falls through to a generic tile, which is the
 * behaviour that keeps a new integration from being invisible.
 */
const SPECIAL_CASED: string[] = ['datto', 'sp', 'sc'];

/** Resend is email delivery, not a backup source. Its own section. */
const COMMS: string[] = ['resend'];

function badgesFor(configured: boolean, enabled: boolean, testOk: boolean | null): TileBadge[] {
  const out: TileBadge[] = [];
  out.push(configured ? { text: 'configured', tone: 'ok' } : { text: 'not configured', tone: 'bad' });
  if (configured && !enabled) out.push({ text: 'disabled', tone: 'warn' });
  if (testOk === true) out.push({ text: 'test passed', tone: 'ok' });
  if (testOk === false) out.push({ text: 'test failed', tone: 'bad' });
  return out;
}

export function IntegrationsPanel({ orgId }: { orgId: string }) {
  const [items, setItems] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** The tile whose dialog is open. One at a time. */
  const [openId, setOpenId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try { setItems((await getIntegrations({ orgId })).integrations); }
    catch (e) { setError(errText(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, [orgId]);

  const sp = items.find((i) => i.source === 'sp');
  const sc = items.find((i) => i.source === 'sc');
  const shadow = sp ?? sc;

  // Everything that is not ShadowControl and not communications. Named
  // sources are handled above; the rest fall through, so a new integration
  // appears the moment the server offers it rather than when somebody
  // remembers to add a line here.
  const sources = items.filter((it) => !SPECIAL_CASED.includes(it.source) && !COMMS.includes(it.source));
  const comms = items.filter((it) => COMMS.includes(it.source));

  const toggle = (id: string) => setOpenId(openId === id ? null : id);
  const close = () => setOpenId(null);

  // The test writes lastTestOk onto whichever source it was called with, so
  // read the NEWER of the two rather than assuming which one ran.
  const newer = (sp?.lastTestAt ?? '') >= (sc?.lastTestAt ?? '') ? (sp ?? sc) : (sc ?? sp);

  return (
    <>
      <section className="panel">
        <h3 className="panel-title">Backup sources</h3>
        {error && <div className="banner banner-error">{error}</div>}

        {/* Only on the FIRST load. Swapping a populated grid for a placeholder
            unmounts every tile, and the open dialog holds its own state -
            which is how a test result could render for one frame and vanish. */}
        {loading && items.length === 0 ? <div className="empty">Loading…</div> : (
          <div className="vgrid">
            {items.filter((it) => it.source === 'datto').map((it) => (
              <VendorTile
                key={it.source} id={it.source} label={it.label} mark={it.mark}
                badges={badgesFor(it.configured, it.enabled, it.lastTestOk)}
                selected={openId === it.source}
                onClick={() => toggle(it.source)}
              />
            ))}

            {/* One appliance, one credential, two sources - so one tile with a
                checkbox each, rather than two tiles asking for the same token
                twice. Which of the two are being collected is on the edit
                screen; repeating it here just crowded the tile. */}
            {shadow && (
              <VendorTile
                key="shadowcontrol" id="sc" label="ShadowProtect · ShadowControl"
                mark={shadow.mark}
                badges={badgesFor(shadow.configured, true, newer?.lastTestOk ?? null)}
                selected={openId === 'shadowcontrol'}
                onClick={() => toggle('shadowcontrol')}
              />
            )}

            {sources.map((it) => (
              <VendorTile
                key={it.source} id={it.source} label={it.label} mark={it.mark}
                badges={badgesFor(it.configured, it.enabled, it.lastTestOk)}
                selected={openId === it.source}
                onClick={() => toggle(it.source)}
              />
            ))}
          </div>
        )}
      </section>

      {/* Email delivery. Not a backup source, and it was confusing sitting
          among them - "configured / test passed" means something quite
          different when the thing being tested is whether an invitation can
          reach a customer. */}
      {comms.length > 0 && (
        <section className="panel" style={{ marginTop: '1rem' }}>
          <h3 className="panel-title">Communications</h3>
          <div className="vgrid">
            {comms.map((it) => (
              <VendorTile
                key={it.source} id={it.source} label={it.label} mark={it.mark}
                badges={badgesFor(it.configured, it.enabled, it.lastTestOk)}
                selected={openId === it.source}
                onClick={() => toggle(it.source)}
              />
            ))}
          </div>
        </section>
      )}

      {/* Inbound mail. Sits beside Resend because it is the same channel
          pointed the other way - one sends, one receives. Rendered outside
          the Resend block: an org that has not configured outbound email
          can still receive backup reports, and hiding this behind Resend
          would make that impossible to discover.*/}
      {orgId && <EmailIngest orgId={orgId} canManage />}

      {/* Unproven adapters, kept structurally apart from the sources that
          actually collect. Nothing here can write to the board. */}
      <VendorRegistryPanel orgId={orgId} kind="collect" />
      <VendorRegistryPanel orgId={orgId} kind="outbound" />
      <PsaPanel orgId={orgId} />

      {/* The dialogs, rendered last so they sit above every panel. */}
      {openId === 'shadowcontrol' && shadow && (
        <ShadowControlDialog orgId={orgId} sp={sp} sc={sc} onChanged={load} onClose={close} />
      )}
      {items.filter((it) => it.source === openId).map((it) => (
        <IntegrationDialog key={it.source} orgId={orgId} item={it} onChanged={load} onClose={close} />
      ))}
    </>
  );
}

/** Everything you can do to one integration. */
function IntegrationDialog({ orgId, item, onChanged, onClose }: {
  orgId: string; item: Integration; onChanged: () => void; onClose: () => void;
}) {
  const [conn, setConn] = useState(DEFAULT_CONNECTION);
  const [baseUrl, setBaseUrl] = useState(item.baseUrl);
  const [values, setValues] = useState<Record<string, string>>({});
  // Never-configured integrations arrive as enabled:true from the server, so
  // the box is ticked when you first open the form. Saving credentials into a
  // switched-off integration was the single most confusing thing here.
  const [enabled, setEnabled] = useState(item.enabled);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Blocks the casual dismissals. A credential lost to a stray backdrop click
  // is a credential typed twice, and the second attempt is where the typo goes.
  const dirty = Object.values(values).some((v) => v !== '') || baseUrl !== item.baseUrl;

  async function run(key: string, fn: () => Promise<unknown>, refresh = true) {
    setBusy(key); setErr(null); setMsg(null);
    try { await fn(); if (refresh) onChanged(); }
    catch (e) { setErr(errText(e)); }
    finally { setBusy(null); }
  }

  return (
    <Modal title={item.label} subtitle={item.baseUrl} onClose={onClose} dirty={dirty}>
      {/* Backup sources can have several connections; Resend is one account
          for the organisation and a second would just be a way to send half
          the mail from the wrong place. */}
      {item.source !== 'resend' && (
        <ConnectionPicker
          orgId={orgId} source={item.source} sourceLabel={item.label}
          selected={conn} onSelect={setConn} onChanged={onChanged}
        />
      )}

      {item.lastTestAt && (
        <div className="dim" style={{ fontSize: 11, marginBottom: 8 }}>
          Last tested {ago(item.lastTestAt)}
          {item.lastTestError ? ` — ${item.lastTestError.slice(0, 160)}` : ''}
        </div>
      )}

      {err && <div className="banner banner-error">{err}</div>}
      {msg && <pre className="banner probe-out">{msg}</pre>}

      <div className="row-form">
        {/* Resend has one endpoint and it is not a choice. */}
        {item.source !== 'resend' && (
          <label className="field">
            <span className="field-label">Base URL</span>
            <input className="field-input" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} />
          </label>
        )}
        {item.fields.map((f) => {
          // A From address is configuration, not a credential: shown in the
          // clear and pre-filled, because config nobody can read back is
          // config nobody can verify.
          const isSecret = f.secret !== false;
          return (
            <label className="field" key={f.key}>
              <span className="field-label">
                {f.label} {isSecret && f.isSet && <span className="dim">stored</span>}
              </span>
              <input
                className="field-input"
                type={isSecret ? 'password' : 'text'}
                autoComplete={isSecret ? 'new-password' : 'off'}
                placeholder={isSecret ? (f.isSet ? 'unchanged' : 'not set') : (f.placeholder ?? '')}
                value={values[f.key] ?? (isSecret ? '' : (f.value ?? ''))}
                onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
              />
            </label>
          );
        })}
      </div>

      <label className="check-row">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        <span>{item.source === 'resend'
          ? 'Send this organisation\'s email from this account'
          : 'Collectors will sync this source for this organisation'}</span>
      </label>

      <div className="row-form" style={{ marginTop: '.6rem' }}>
        <button
          className="btn btn-primary" disabled={busy !== null}
          onClick={() => run('save', async () => {
            const r = await setIntegration({ orgId, source: item.source, connectionKey: conn, enabled, baseUrl, values });
            setValues({});
            setMsg(r.fieldsWritten.length
              ? `Saved. Updated: ${r.fieldsWritten.join(', ')}.`
              : 'Saved. No credential fields changed.');
          })}
        >
          {busy === 'save' ? 'Saving…' : 'Save'}
        </button>

        <button
          className="btn" disabled={busy !== null || !item.configured}
          title={item.configured ? 'Make a real call to the vendor' : 'Configure credentials first'}
          onClick={() => run('test', async () => {
            const r = await testIntegration({ orgId, source: item.source, connectionKey: conn });
            setMsg(r.ok ? `Connection OK — ${r.detail}` : `Failed: ${r.error}`);
          })}
        >
          {busy === 'test' ? 'Testing…' : 'Test connection'}
        </button>

        {/* Datto only. Gathers the evidence for schedule-aware status. */}
        {item.source === 'datto' && (
          <button
            className="btn" disabled={busy !== null || !item.configured}
            title="Read what Datto reports per agent. Read-only."
            onClick={() => run('probe', async () => {
              const r = await probeDatto({ orgId });
              setMsg(r.report);
            }, false)}
          >
            {busy === 'probe' ? 'Probing…' : 'Probe agent schedule'}
          </button>
        )}

        {/* MSP360 only. Resolves the undocumented status enum. */}
        {item.source === 'msp' && (
          <button
            className="btn" disabled={busy !== null || !item.configured}
            title="Read the status codes from live data. Read-only."
            onClick={() => run('probe', async () => {
              const r = await probeMsp({ orgId });
              const pairs = Object.entries(r.statusMap ?? {})
                .map(([k, v]) => k + ' = ' + v)
                .join('\n');
              setMsg(
                r.ok
                  ? r.message + '\n\nSTATUS CODES OBSERVED (integer = vendor name)\n' +
                    (pairs || '(no rows carried a status)') +
                    '\n\nONE COMPLETE ROW\n' + r.sample
                  : r.message
              );
            }, false)}
          >
            {busy === 'probe' ? 'Probing…' : 'Resolve status codes'}
          </button>
        )}

        <button
          className="btn" disabled={busy !== null || !item.configured}
          onClick={() => run('clear', async () => {
            await clearIntegration({ orgId, source: item.source, connectionKey: conn });
            setMsg('Credentials removed.');
          })}
        >
          Clear
        </button>
      </div>
    </Modal>
  );
}

/**
 * ShadowControl: one credential, two collectable sources.
 *
 * Saving writes the SAME credential values to both source records, because
 * that is the truth of it — the appliance does not have separate logins for
 * endpoints and Image Manager. The checkboxes drive each source's `enabled`
 * flag independently, so turning off Image Manager stops collecting it without
 * touching the credential or the endpoint sync.
 */
function ShadowControlDialog({ orgId, sp, sc, onChanged, onClose }: {
  orgId: string;
  sp: Integration | undefined;
  sc: Integration | undefined;
  onChanged: () => void;
  onClose: () => void;
}) {
  const primary = sp ?? sc!;
  const [baseUrl, setBaseUrl] = useState(primary.baseUrl);
  const [values, setValues] = useState<Record<string, string>>({});
  const [conn, setConn] = useState(DEFAULT_CONNECTION);
  const [pullSp, setPullSp] = useState(sp?.enabled ?? false);
  const [pullSc, setPullSc] = useState(sc?.enabled ?? false);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const dirty = Object.values(values).some((v) => v !== '') || baseUrl !== primary.baseUrl;

  async function run(key: string, fn: () => Promise<unknown>, refresh = true) {
    setBusy(key); setErr(null); setMsg(null);
    try { await fn(); if (refresh) onChanged(); }
    catch (e) { setErr(errText(e)); }
    finally { setBusy(null); }
  }

  const newer = (sp?.lastTestAt ?? '') >= (sc?.lastTestAt ?? '') ? (sp ?? sc) : (sc ?? sp);
  const configured = primary.configured;

  return (
    <Modal title="ShadowProtect · ShadowControl" subtitle={primary.baseUrl} onClose={onClose} dirty={dirty}>
      {/* ShadowControl is a physical box, so several is the normal case for
          an MSP with sites in more than one city. */}
      <ConnectionPicker
        orgId={orgId} source="sp" sourceLabel="ShadowControl"
        selected={conn} onSelect={setConn} onChanged={onChanged}
      />

      {newer?.lastTestAt && (
        <div className="dim" style={{ fontSize: 11, marginBottom: 8 }}>
          Last tested {ago(newer.lastTestAt)}
          {newer.lastTestError ? ` — ${newer.lastTestError.slice(0, 160)}` : ''}
        </div>
      )}

      {err && <div className="banner banner-error">{err}</div>}
      {msg && <pre className="banner probe-out">{msg}</pre>}

      <div className="row-form">
        <label className="field">
          <span className="field-label">Base URL</span>
          <input className="field-input" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} />
        </label>
        {primary.fields.map((f) => (
          <label className="field" key={f.key}>
            <span className="field-label">
              {f.label} {f.isSet && <span className="dim">stored</span>}
            </span>
            <input
              className="field-input" type="password" autoComplete="new-password"
              placeholder={f.isSet ? 'unchanged' : 'not set'}
              value={values[f.key] ?? ''}
              onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
            />
          </label>
        ))}
      </div>

      <div className="flbl" style={{ marginTop: 10 }}>Collect from this appliance</div>
      <label className="check-line">
        <input type="checkbox" checked={pullSp} onChange={(e) => setPullSp(e.target.checked)} />
        <span>ShadowProtect endpoints</span>
      </label>
      <label className="check-line">
        <input type="checkbox" checked={pullSc} onChange={(e) => setPullSc(e.target.checked)} />
        <span>Image Manager</span>
      </label>

      <div className="row-form" style={{ marginTop: '.6rem' }}>
        <button
          className="btn btn-primary" disabled={busy !== null}
          onClick={() => run('save', async () => {
            // Same credential to both records: one appliance, one login.
            const a = await setIntegration({ orgId, source: 'sp', connectionKey: conn, enabled: pullSp, baseUrl, values });
            await setIntegration({ orgId, source: 'sc', connectionKey: conn, enabled: pullSc, baseUrl, values });
            setValues({});
            const on = [pullSp && 'endpoints', pullSc && 'Image Manager'].filter(Boolean).join(' and ');
            setMsg(
              (a.fieldsWritten.length ? `Saved. Updated: ${a.fieldsWritten.join(', ')}. ` : 'Saved. ') +
              (on ? `Collecting ${on}.` : 'Nothing selected — this appliance will not be collected.')
            );
          })}
        >
          {busy === 'save' ? 'Saving…' : 'Save'}
        </button>

        <button
          className="btn" disabled={busy !== null || !configured}
          onClick={() => run('test', async () => {
            const r = await testIntegration({ orgId, source: 'sp', connectionKey: conn });
            setMsg(r.ok ? `Connection OK — ${r.detail}` : `Failed: ${r.error}`);
          })}
        >
          {busy === 'test' ? 'Testing…' : 'Test connection'}
        </button>

        <button
          className="btn" disabled={busy !== null}
          title="Try the Report API token. Read-only — changes nothing."
          onClick={() => run('probe', async () => {
            const r = await probeToken({ orgId, source: 'sp' });
            setMsg(
              r.ok
                ? `${r.message}\n\n` +
                  `LIVE NOW\nSP  ${r.liveSplit.sp}\nSC  ${r.liveSplit.sc}\n\n` +
                  `CENSUS - the raw distribution\n${r.census}\n\n` +
                  `RAW SCHEDULE, as the vendor sends it\n${r.rawSchedule}\n\n` +
                  `SCHEDULE MODEL — what would CHANGE (now -> proposed, worst-of applied)\n${r.schedDiff}\n\n` +
                  `SCHEDULE ANOMALIES — worth a human glance\n${r.anomalies}\n\n` +
                  `SHADOWPROTECT — live status | what the token says\n${r.spDiff}\n\n` +
                  `IMAGE MANAGER - folder shape, states and real alert text\n${r.folderDetail}`
                : r.message
            );
          }, false)}
        >
          {busy === 'probe' ? 'Probing…' : 'Probe token'}
        </button>

        <button
          className="btn" disabled={busy !== null || !configured}
          title="Ask the CMD server what this token can reach. Changes nothing."
          onClick={() => run('actions', async () => {
            const r = await probeScActions({ orgId });
            setMsg(r.report);
          }, false)}
        >
          {busy === 'actions' ? 'Asking…' : 'What can this token do?'}
        </button>

        <button
          className="btn" disabled={busy !== null || !configured}
          onClick={() => run('clear', async () => {
            await clearIntegration({ orgId, source: 'sp', connectionKey: conn });
            await clearIntegration({ orgId, source: 'sc', connectionKey: conn });
            setMsg('Credentials removed.');
          })}
        >
          Clear
        </button>
      </div>
    </Modal>
  );
}
